import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const securityFeatures = [
  {
    icon: Shield,
    title: 'XtreamGate 2026',
    description: 'This verified gateway was launched to protect users from cloned websites and phishing attempts.'
  },
  {
    icon: Lock,
    title: 'HTTPS & Padlock',
    description: 'Always verify the address bar shows a secure connection with https:// and padlock icon.'
  },
  {
    icon: AlertTriangle,
    title: 'Beware of Clones',
    description: 'If you find unauthorized XtreamCube clones, return to xtreamgate.com for verified XtreamCube backup access.'
  }
];

const verifiedDomains = [
  { name: 'xtreamcube.shop', label: 'backup 1', url: 'https://xtreamcube.shop' },
  { name: 'xtreamcube.xyz', label: 'backup 2', url: 'https://xtreamcube.xyz' },
  { name: 'xtreamcube.online', label: 'backup 3', url: 'https://xtreamcube.online' }
];

export const SecuritySection = () => {
  return (
    <section className="py-16 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Main security section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="glass rounded-2xl p-8 md:p-10 space-y-8"
          style={{ boxShadow: 'var(--shadow-glass)' }}
        >
          <div className="text-center space-y-3">
            <h2 className="text-3xl sm:text-4xl font-bold">
              How to Verify You're on Official XtreamCube
            </h2>
          </div>

          {/* Security features grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {securityFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 + index * 0.15 }}
                className="space-y-3"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold">{feature.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Verified domains */}
          <div className="pt-6 border-t border-border space-y-5">
            <div className="flex items-center justify-center gap-2 text-primary">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">Verified Backup Domains</span>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3">
              {verifiedDomains.map((domain, index) => (
                <motion.div
                  key={domain.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
                >
                  <Button
                    asChild
                    variant="outline"
                    className="glass border-primary/30 hover:bg-primary/10 text-foreground hover:text-foreground"
                  >
                    <a href={domain.url} target="_blank" rel="noopener noreferrer">
                      {domain.name}
                      <span className="ml-2 text-xs text-primary/70">({domain.label})</span>
                    </a>
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Disclaimer */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="pt-6 border-t border-border text-center space-y-2"
          >
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">Disclaimer:</span> For your own safety,
              visit only the <span className="text-primary font-medium">verified XtreamCube domains</span> listed
              on this gateway. We guarantee top‑notch quality of service exclusively on these official properties.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};